import dayjs from "dayjs";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import {
  SearchParticipantResult,
  SearchParticipantResultFormKey,
} from "../../../../modelsAndSchemas/Models";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  ChipOptionsDataType,
  FormController,
  MuiBox,
  MuiCheckbox,
  MuiDateField,
  MuiSelect,
  MuiTable,
  MuiTextFieldWithSearchIcon,
  MuiTypography,
  RowProps,
} from "../../../../../../components";
import MuiChipWithPopover from "../../../../../../components/chipWithPopover/MuiChipWithPopover";
import { MuiSelectedList } from "../../../../../../components/selectedList/MuiSelectedList";
import { StatusBadge } from "../../../../../../components/statusBadge/StatusBadge";
import { MAX_LENGTH_75 } from "../../../../../../helpers/constants";
import { commonSiteCoreKeys } from "../../../../../../helpers/constants/siteCoreKeys";
import { LabelValue } from "../../../../../../helpers/types/common";
import {
  compareDates,
  convertToUserFormat,
  getFullName,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../../redux/slices/appState/appStateSelector";
import { bulkActivitySelectedParticipantsSelector } from "../../../../../../redux/slices/program/programSelector";
import { setBulkActivitySelectedParticipants } from "../../../../../../redux/slices/program/programSlice";
import { bulkActivitySiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { SearchParticipantsResultWrapper } from "./SearchParticipantsResult.style";
import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { getDateFormatByCountry } from "../../../../../../helpers/constants/countryDateFormat";

interface SearchParticipantsResultProps {
  rows: SearchParticipantResult[];
  pageId: string;
  setShowInformationModal?: (value: boolean) => void;
  currentStep?: number;
}

export const SearchParticipantsResult: FC<SearchParticipantsResultProps> = ({
  rows,
  pageId,
  setShowInformationModal,
  currentStep = 0,
}) => {
  const dispatch = useDispatch();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  const selectedParticipants = useSelector(
    bulkActivitySelectedParticipantsSelector,
  );

  const { fetchLabel, commonLabels, fetchValueByKeyOfDropdown } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

  const tableRef = React.useRef<{ resetPagination: () => void }>(null);
  const [filteredResults, setFilteredResults] = useState([...rows]);

  const [allUpdatedRows, setAllUpdatedRows] = useState<
    SearchParticipantResult[]
  >([]);
  const [uniqueLineOfBusinesses, setUniqueLineOfBusinesses] = useState<
    LabelValue[]
  >([]);
  const [uniqueTemplates, setUniqueTemplates] = useState<LabelValue[]>([]);
  const [uniqueDeliveryCountries, setUniqueDeliveryCountries] = useState<
    LabelValue[]
  >([]);
  const [uniqueDeliveryOffice, setUniqueDeliveryOffice] = useState<
    LabelValue[]
  >([]);
  const [uniqueStatus, setUniqueStatus] = useState<LabelValue[]>([]);

  const getResultLabel = (key: string) => {
    return fetchLabel(
      bulkActivitySiteCoreKeys.searchParticipantsResultsKey,
      key,
    );
  };

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const defaultSearchParticipantResult = {
    [SearchParticipantResultFormKey.participantName]: "",
    [SearchParticipantResultFormKey.programName]: "",
    [SearchParticipantResultFormKey.companyName]: "",
    [SearchParticipantResultFormKey.programTemplateName]: "",
    [SearchParticipantResultFormKey.deliveryCountryName]: "",
    [SearchParticipantResultFormKey.deliveryOfficeName]: "",
    [SearchParticipantResultFormKey.status]: "",
    [SearchParticipantResultFormKey.lobName]: "",
    [SearchParticipantResultFormKey.coach]: "",
    [SearchParticipantResultFormKey.createdDate]: "",
  };

  const methods = useForm({
    mode: "all",
    defaultValues: defaultSearchParticipantResult,
  });

  const { reset, control, getValues, setValue } = methods;

  const filters = useWatch({ control });

  useEffect(() => {
    resetPagination();
    const myData = structuredClone(rows);

    const uniqueLineOfBusiness = Array.from(
      new Set(myData.map((item) => item.lobName)),
    ).map((label) => ({
      value: label,
      label: label,
    }));

    const uniqueTemplate = Array.from(
      new Set(myData.map((item) => item.programTemplateName)),
    ).map((label) => ({
      value: label,
      label: label,
    }));

    const uniqueCountries = Array.from(
      new Set(myData.map((item) => item.deliveryCountryName)),
    ).map((country) => ({ value: country, label: country }));

    setUniqueDeliveryCountries(uniqueCountries);
    setUniqueLineOfBusinesses(uniqueLineOfBusiness);
    setUniqueTemplates(uniqueTemplate);
    setIsAllSelected(false);

    reset();
  }, [rows]);

  useEffect(() => {
    if (!siteCoreLoading) {
      const localData = structuredClone(rows);

      localData.forEach((data: SearchParticipantResult) => {
        data.statusKey = data.status;
        data.status = fetchValueByKeyOfDropdown(
          bulkActivitySiteCoreKeys.searchParticipantsResultsKey,
          bulkActivitySiteCoreKeys.resultsStatusList,
          data.status,
        );

        data.participantName = getFullName(
          data.participantFirstName,
          data.participantLastName,
          true,
        );

        data.coachesList = data.coaches.map((coach) => {
          return getFullName(coach.coachFirstName, coach.coachLastName, true);
        });
        data.createdDate = data.createdTimeStamp;
        data.isSelected = false;
      });

      const uniqueOffices = Array.from(
        new Set(localData.map((item) => item.deliveryOfficeName)),
      ).map((label) => ({ value: label, label: label }));

      const uniqueStatus = Array.from(
        new Set(localData.map((item) => item.status)),
      ).map((label) => ({ value: label, label: label }));

      setUniqueDeliveryOffice(uniqueOffices);
      setUniqueStatus(uniqueStatus);
      setAllUpdatedRows(localData);
      setFilteredResults([...localData]);
    }
  }, [rows, siteCoreLoading]);

  const checkFilterMatch = (inputValue: string, rowItem: string) => {
    return isEmpty(inputValue?.toLowerCase())
      ? true
      : !isEmpty(rowItem) &&
          rowItem?.toLowerCase().includes(inputValue?.toLowerCase());
  };

  useEffect(() => {
    if (isNotEmpty(allUpdatedRows)) {
      const {
        participantName,
        programName,
        companyName,
        lobName,
        programTemplateName,
        status,
        deliveryCountryName,
        deliveryOfficeName,
        coach,
        createdDate,
      } = getValues();

      const myData = allUpdatedRows.filter((item: SearchParticipantResult) => {
        const programNameMatch = checkFilterMatch(
          programName,
          item.programName,
        );

        const participantNameMatch = checkFilterMatch(
          participantName,
          item.participantName ?? "",
        );

        const companyNameMatch = checkFilterMatch(companyName, item.clientName);

        const lineOfBusinessMatch = checkFilterMatch(lobName, item.lobName);

        const programTemplateMatch = checkFilterMatch(
          programTemplateName,
          item.programTemplateName,
        );

        const coachMatch =
          !coach ||
          item.coachesList.some((c: string) =>
            c.toLowerCase().includes(coach.toLowerCase()),
          );

        let isDateMatch = compareDates(
          createdDate ?? "",
          item?.createdDate ?? "",
          getValueOrBlank(userCountryDateFormat),
        );

        const statusAll = fetchValueByKeyOfDropdown(
          bulkActivitySiteCoreKeys.searchParticipantsKey,
          bulkActivitySiteCoreKeys.resultsStatusList,
          commonSiteCoreKeys.statusAll,
        );

        const statusMatch =
          status === "" ||
          status === statusAll ||
          item.status?.toLowerCase() === status?.toLowerCase();

        const deliveryCountryMatch = checkFilterMatch(
          deliveryCountryName,
          item.deliveryCountryName,
        );

        const deliveryOfficeMatch = checkFilterMatch(
          deliveryOfficeName,
          item.deliveryOfficeName,
        );

        return (
          programNameMatch &&
          participantNameMatch &&
          companyNameMatch &&
          lineOfBusinessMatch &&
          programTemplateMatch &&
          statusMatch &&
          deliveryCountryMatch &&
          deliveryOfficeMatch &&
          coachMatch &&
          isDateMatch
        );
      });
      setFilteredResults([...myData]);
      resetPagination();
      setAllCheckboxSelected(myData);
    }
  }, [filters]);

  const storeSelectedDataInRedux = (
    filteredResults: SearchParticipantResult[],
  ) => {
    dispatch(setBulkActivitySelectedParticipants(filteredResults));
  };

  const setAllCheckboxSelected = (updatedRows: SearchParticipantResult[]) => {
    // Check if all rows have isSelected as true
    const allSelected = updatedRows.every(
      (row: SearchParticipantResult) => row.isSelected === true,
    );
    setIsAllSelected(updatedRows.length ? allSelected : false);
  };

  const handleRowCheckboxChange = (checked: boolean, rowId: string) => {
    // Update only the clicked row's `isSelected` value

    const updatedRows = filteredResults.map((row: SearchParticipantResult) =>
      row.participantId === rowId ? { ...row, isSelected: checked } : row,
    );
    setFilteredResults([...updatedRows]);

    setAllCheckboxSelected(updatedRows);

    const updatedAllRows = allUpdatedRows.map((row) =>
      row.participantId === rowId ? { ...row, isSelected: checked } : row,
    );
    setAllUpdatedRows([...updatedAllRows]);
    storeSelectedDataInRedux([...updatedAllRows]);
  };

  const onHeaderCheckBoxChange = (checked: boolean) => {
    setIsAllSelected(checked);
    const updatedRows = filteredResults.map((row: SearchParticipantResult) => ({
      ...row,
      isSelected: checked,
    }));

    setFilteredResults([...updatedRows]);

    // Also update `allUpdatedRows` to reflect changes for the whole dataset
    const updatedAllRows = allUpdatedRows.map(
      (row: SearchParticipantResult) => {
        const matchingRow = updatedRows.find(
          (updatedRow) => updatedRow.participantId === row.participantId,
        );
        return matchingRow
          ? { ...row, isSelected: matchingRow.isSelected }
          : row;
      },
    );

    // Update `allUpdatedRows` state
    setAllUpdatedRows([...updatedAllRows]);
    storeSelectedDataInRedux([...updatedAllRows]);
  };

  // const selectedCount = useMemo(() => {
  //   return allUpdatedRows.filter(
  //     (row: SearchParticipantResult) => row.isSelected,
  //   ).length;
  // }, [allUpdatedRows]);

  //to reset deleted participants as uncheck if user came from second tab
  useEffect(() => {
    if (!selectedParticipants.length) return;
    const updatedFilteredResults = filteredResults.map((row) => {
      const match = selectedParticipants.find(
        (p) => p.participantId === row.participantId,
      );
      return match ? { ...row, isSelected: match.isSelected } : row;
    });
    const updatedRows = allUpdatedRows.map((row) => {
      const match = selectedParticipants.find(
        (p) => p.participantId === row.participantId,
      );
      return match ? { ...row, isSelected: match.isSelected } : row;
    });
    setFilteredResults(updatedFilteredResults);
    setAllCheckboxSelected(updatedFilteredResults);
    setAllUpdatedRows(updatedRows);
  }, [currentStep]);

  const columns = [
    {
      id: SearchParticipantResultFormKey.isSelected,
      label: "",
      className: "checkbox-cell",
      isSortVisible: false,
      headerRender: () => (
        <MuiCheckbox
          checked={isAllSelected}
          checkboxLabel={`${commonARIALabel?.selectAll}`}
          hideLabel={true}
          onCheckedChange={(checked: boolean) =>
            onHeaderCheckBoxChange(checked)
          }
        />
      ),
      render: (row: RowProps) => {
        return (
          <MuiCheckbox
            checked={row.isSelected}
            checkboxLabel={`${row.name}`}
            hideLabel={true}
            id={`${pageId}-${row.participantId}`}
            onCheckedChange={(checked: boolean) => {
              handleRowCheckboxChange(checked, row.participantId);
            }}
            key={`${pageId}-${row.participantId}-${row.isSelected}`}
          />
        );
      },
    },
    {
      id: SearchParticipantResultFormKey.participantName,
      label: getResultLabel(bulkActivitySiteCoreKeys.resultParticipantName),
      isEmptyValue: true,
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiTypography variant="h3" className="cell">
          {row.participantName}
        </MuiTypography>
      ),
    },
    {
      id: SearchParticipantResultFormKey.programName,
      label: getResultLabel(bulkActivitySiteCoreKeys.resultsProgramName),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: SearchParticipantResultFormKey.clientName,
      label: getResultLabel(bulkActivitySiteCoreKeys.resultClient),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: SearchParticipantResultFormKey.lobName,
      label: getResultLabel(bulkActivitySiteCoreKeys.resultsProductLine),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: SearchParticipantResultFormKey.programTemplateName,
      label: getResultLabel(bulkActivitySiteCoreKeys.resultsProgramTemplate),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: SearchParticipantResultFormKey.status,
      label: getResultLabel(bulkActivitySiteCoreKeys.resultsStatus),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell",
      render: (row: RowProps) => (
        <StatusBadge status={row.statusKey} label={row.status} />
      ),
    },
    {
      id: SearchParticipantResultFormKey.deliveryCountryName,
      label: getResultLabel(bulkActivitySiteCoreKeys.resultsDeliveryCountry),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: SearchParticipantResultFormKey.deliveryOfficeName,
      label: getResultLabel(bulkActivitySiteCoreKeys.resultsDeliveryOffice),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: SearchParticipantResultFormKey.coachesList,
      label: getResultLabel(bulkActivitySiteCoreKeys.resultsCoach),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: RowProps) => (
        <MuiChipWithPopover
          data={
            row.coachesList?.map((item: string) => ({
              label: item,
              value: item,
            })) as ChipOptionsDataType[]
          }
          id={"selected-coaches"}
          isEditable={false}
          headerLabel={getResultLabel(bulkActivitySiteCoreKeys.resultsCoach)}
          gridRef={React.createRef<HTMLElement>()}
          countToWrap={1}
          showMoreARIALabel={commonARIALabel.showMoreDetails}
          removeChipNameARIALabel={commonARIALabel.removeChipName}
          closePopoverARIALabel={commonARIALabel.closePopover}
        />
      ),
    },
    {
      id: SearchParticipantResultFormKey.createdDate,
      label: getResultLabel(bulkActivitySiteCoreKeys.resultsCreatedDate),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: RowProps) => {
        return (
          <>{convertToUserFormat(row.createdDate, userCountryDateFormat)}</>
        );
      },
    },
  ];

  const tableHeaderForm = [
    <></>,
    <FormController
      control={control}
      key={SearchParticipantResultFormKey.participantName}
      name={SearchParticipantResultFormKey.participantName}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${SearchParticipantResultFormKey.participantName}`}
        placeholder={commonLabels.search}
        showCloseIcon
        maxLength={MAX_LENGTH_75}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <FormController
      control={control}
      key={SearchParticipantResultFormKey.programName}
      name={SearchParticipantResultFormKey.programName}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${SearchParticipantResultFormKey.programName}`}
        placeholder={commonLabels.search}
        showCloseIcon
        maxLength={MAX_LENGTH_75}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <FormController
      control={control}
      key={SearchParticipantResultFormKey.companyName}
      name={SearchParticipantResultFormKey.companyName}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${SearchParticipantResultFormKey.companyName}`}
        placeholder={commonLabels.search}
        showCloseIcon
        maxLength={MAX_LENGTH_75}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <FormController
      control={control}
      key={SearchParticipantResultFormKey.lobName}
      name={SearchParticipantResultFormKey.lobName}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption
        options={uniqueLineOfBusinesses}
        value=""
        id={`${pageId}-${SearchParticipantResultFormKey.lobName}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchParticipantResultFormKey.programTemplateName}
      key={SearchParticipantResultFormKey.programTemplateName}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption
        options={uniqueTemplates}
        value=""
        id={`${pageId}-${SearchParticipantResultFormKey.programTemplateName}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key={SearchParticipantResultFormKey.status}
      name={SearchParticipantResultFormKey.status}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption
        options={uniqueStatus}
        value=""
        id={`${pageId}-${SearchParticipantResultFormKey.status}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key={SearchParticipantResultFormKey.deliveryCountryName}
      name={SearchParticipantResultFormKey.deliveryCountryName}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={uniqueDeliveryCountries}
        value=""
        id={`${pageId}-${SearchParticipantResultFormKey.deliveryCountryName}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
        showSearch
        searchPlaceholder={commonLabels.search}
      />
    </FormController>,
    <FormController
      control={control}
      key={SearchParticipantResultFormKey.deliveryOfficeName}
      name={SearchParticipantResultFormKey.deliveryOfficeName}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={uniqueDeliveryOffice}
        value=""
        id={`${pageId}-${SearchParticipantResultFormKey.deliveryOfficeName}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchParticipantResultFormKey.coach}
      key={SearchParticipantResultFormKey.coach}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${SearchParticipantResultFormKey.coach}`}
        placeholder={commonLabels.search}
        showCloseIcon
        maxLength={MAX_LENGTH_75}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <FormController
      control={control}
      key={SearchParticipantResultFormKey.createdDate}
      name={SearchParticipantResultFormKey.createdDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          format={userCountryDateFormat}
          name={SearchParticipantResultFormKey.createdDate}
          clearable
          value={
            getValues(SearchParticipantResultFormKey.createdDate) !== ""
              ? dayjs(
                  getValues(SearchParticipantResultFormKey.createdDate),
                  userCountryDateFormat,
                )
              : null
          }
          onChange={(newValue) => {
            setValue(
              SearchParticipantResultFormKey.createdDate,
              getValueOrBlank(newValue?.format(userCountryDateFormat)),
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
  ];

  const handleRemove = (id: number | string) => {
    handleRowCheckboxChange(false, id as string);
  };

  const SelectedItems = useMemo(() => {
    return selectedParticipants
      .filter((participant) => participant.isSelected)
      .map((item) => ({
        label: item.participantName ?? "",
        value: item.participantId,
      }));
  }, [selectedParticipants]);

  return (
    <SearchParticipantsResultWrapper>
      <MuiBox className="search-result-header-wrapper">
        <MuiTypography
          variant="h2SemiBold"
          className="search-result-header"
          component="div"
        >
          {getResultLabel(bulkActivitySiteCoreKeys.chooseParticipants)}
        </MuiTypography>
        <MuiBox className="search-header-right">
          {/* <MuiTypography variant="h4Medium" className="search-selected">
            {getResultLabel(bulkActivitySiteCoreKeys.resultsSelected)}
          </MuiTypography>
          <MuiTypography variant="h3Bold" className="selected-count">
            {selectedCount}
          </MuiTypography> */}

          <MuiSelectedList
            selectedItems={SelectedItems}
            onRemove={handleRemove}
            id="participants-select"
            label={replaceHashWithValue(
              getResultLabel(
                bulkActivitySiteCoreKeys.participantsSelectedDropDownText,
              ),
              String(SelectedItems?.length ?? 0),
            )}
            requiredARIALabel={commonARIALabel.required}
          />
        </MuiBox>
      </MuiBox>

      <MuiTable
        ref={tableRef}
        pageId={pageId}
        tableHeaderForm={tableHeaderForm}
        columns={columns}
        tableResults={[...filteredResults]}
        noDataInApi={filteredResults.length === 0}
        noResultMessage={commonLabels.noResult}
        priorityResults={[]}
        rowIdKey="participantId"
        emptyMessageColSpan={8}
      />
    </SearchParticipantsResultWrapper>
  );
};
